import { z } from "zod";

export const RESULTS_DISK_MOUNT_PATH = "/mnt/tool-results" as const;

export const RESULTS_DISK_SLOT = "results" as const;

export const fileSystemScopeSchema = z.enum(["user", "global"]);

export const fileSystemAccessSchema = z.enum(["read", "write"]);

export const fileSystemIdSchema = z.string().min(1).max(512);

export const fileSystemRefSchema = z
    .object({
        id: fileSystemIdSchema,
    })
    .strict();

export const fileSystemSlotSchema = z
    .string()
    .min(1)
    .max(64)
    .regex(/^[a-z][a-z0-9-]*$/u);

export const fileSystemMountPathSchema = z
    .string()
    .min(2)
    .max(4_096)
    .startsWith("/")
    .refine(
        (path) =>
            !path.includes("\0") &&
            !path.split("/").some((segment) => segment === "..") &&
            path.startsWith("/mnt/"),
        {
            message: "Filesystem mounts must target a child of /mnt",
        },
    )
    .refine((path) => normalizedMountPath(path) === path, {
        message: "Filesystem mount path must be canonical",
    });

export const fileSystemRootPathsSchema = z.record(
    fileSystemIdSchema,
    z
        .string()
        .min(2)
        .max(4_096)
        .startsWith("/")
        .refine(
            (path) =>
                !path.includes("\0") &&
                !path.split("/").some((segment) => segment === "..") &&
                normalizedMountPath(path) === path,
            { message: "Filesystem root path must be canonical and absolute" },
        ),
);

export const fileSystemExpectedBindingSchema = z.discriminatedUnion("kind", [
    z
        .object({
            kind: z.literal("literal"),
            value: z.string().min(1).max(256),
        })
        .strict(),
    z
        .object({
            kind: z.literal("context"),
            field: z
                .string()
                .min(1)
                .max(64)
                .regex(/^[A-Za-z_][A-Za-z0-9_]*$/u),
        })
        .strict(),
]);

const fileSystemRequirementBaseSchema = z
    .object({
        access: fileSystemAccessSchema,
        inheritToChildren: z.boolean().optional(),
        mountPath: fileSystemMountPathSchema,
        required: z.boolean().optional(),
        expectedBinding: fileSystemExpectedBindingSchema.optional(),
    })
    .strict();

export function isResultsDiskPath(path: string): boolean {
    return (
        path === RESULTS_DISK_MOUNT_PATH ||
        path.startsWith(`${RESULTS_DISK_MOUNT_PATH}/`)
    );
}

export function isCallerFileSystemMountPath(mountPath: string): boolean {
    return !isResultsDiskPath(mountPath);
}

export function isResultsDiskSlot(slot: string): boolean {
    return slot === RESULTS_DISK_SLOT;
}

export const fileSystemRequirementSchema =
    fileSystemRequirementBaseSchema.refine(
        ({ mountPath }) => isCallerFileSystemMountPath(mountPath),
        {
            message: "The /mnt/tool-results path is reserved for tool results",
            path: ["mountPath"],
        },
    );

const authoredFileSystemRequirementBaseSchema = fileSystemRequirementBaseSchema
    .omit({ mountPath: true })
    .extend({
        mountPath: fileSystemMountPathSchema.optional(),
    })
    .strict();

export const authoredFileSystemRequirementSchema =
    authoredFileSystemRequirementBaseSchema.refine(
        ({ mountPath }) =>
            mountPath === undefined || isCallerFileSystemMountPath(mountPath),
        {
            message: "The /mnt/tool-results path is reserved for tool results",
            path: ["mountPath"],
        },
    );

export const authoredFileSystemRequirementsSchema = z
    .record(fileSystemSlotSchema, authoredFileSystemRequirementSchema)
    .superRefine((requirements, context) => {
        rejectReservedResultsSlot(requirements, context);
        const entries = Object.entries(requirements);
        if (entries.length > 16) {
            context.addIssue({
                code: "too_big",
                maximum: 16,
                origin: "object",
                inclusive: true,
                message: "Agents may declare at most 16 filesystems",
            });
        }
        uniqueNonOverlappingMounts(
            entries.flatMap(([, requirement]) =>
                requirement.mountPath === undefined
                    ? []
                    : [{ mountPath: requirement.mountPath }],
            ),
            context,
        );
    });

export const resolvedFileSystemMountSchema = fileSystemRequirementBaseSchema
    .omit({
        expectedBinding: true,
        inheritToChildren: true,
        required: true,
    })
    .extend({
        fileSystem: fileSystemRefSchema,
        slot: fileSystemSlotSchema,
    })
    .strict()
    .superRefine((mount, context) => {
        const resultsSlot = isResultsDiskSlot(mount.slot);
        const resultsPath = mount.mountPath === RESULTS_DISK_MOUNT_PATH;
        if (resultsSlot) {
            if (!resultsPath) {
                context.addIssue({
                    code: "custom",
                    message: "The results slot must mount at /mnt/tool-results",
                    path: ["mountPath"],
                });
            }
            if (mount.access !== "write") {
                context.addIssue({
                    code: "custom",
                    message: "The results slot requires write access",
                    path: ["access"],
                });
            }
            return;
        }
        if (isResultsDiskPath(mount.mountPath)) {
            context.addIssue({
                code: "custom",
                message:
                    "The /mnt/tool-results path is reserved for tool results",
                path: ["mountPath"],
            });
        }
    });

export const fileSystemMountSchema = z
    .object({
        access: fileSystemAccessSchema,
        fileSystemId: fileSystemIdSchema,
        mountPath: fileSystemMountPathSchema,
        provider: z.literal("archil").optional(),
        retention: z.enum(["session", "retained"]),

        scope: fileSystemSlotSchema,
    })
    .strict();

function normalizedMountPath(path: string): string {
    const segments: string[] = [];
    for (const segment of path.split("/")) {
        if (!segment || segment === ".") continue;
        if (segment === "..") segments.pop();
        else segments.push(segment);
    }
    return `/${segments.join("/")}`;
}

function uniqueNonOverlappingMounts(
    mounts: readonly { mountPath: string }[],
    context: z.RefinementCtx,
): void {
    const normalized = mounts.map((mount) =>
        normalizedMountPath(mount.mountPath),
    );
    for (let index = 0; index < normalized.length; index += 1) {
        for (let other = 0; other < index; other += 1) {
            const left = normalized[index];
            const right = normalized[other];
            if (
                left === right ||
                left.startsWith(`${right}/`) ||
                right.startsWith(`${left}/`)
            ) {
                context.addIssue({
                    code: "custom",
                    message: "Filesystem mount paths must not overlap",
                    path: [index, "mountPath"],
                });
            }
        }
    }
}

function rejectReservedResultsDiskPaths(
    mounts: readonly { mountPath: string }[],
    context: z.RefinementCtx,
): void {
    for (const [index, mount] of mounts.entries()) {
        if (!isResultsDiskPath(mount.mountPath)) continue;
        context.addIssue({
            code: "custom",
            message: "The /mnt/tool-results path is reserved for tool results",
            path: [index, "mountPath"],
        });
    }
}

function rejectReservedResultsSlot(
    requirements: Record<string, unknown>,
    context: z.RefinementCtx,
): void {
    if (!Object.hasOwn(requirements, RESULTS_DISK_SLOT)) return;
    context.addIssue({
        code: "custom",
        message: "The results slot is reserved for tool results",
        path: [RESULTS_DISK_SLOT],
    });
}

function uniqueScopes(
    mounts: readonly { scope: string }[],
    context: z.RefinementCtx,
): void {
    const scopes = new Set<string>();
    for (const [index, mount] of mounts.entries()) {
        if (scopes.has(mount.scope)) {
            context.addIssue({
                code: "custom",
                message: "Filesystem scopes must be unique within a sandbox",
                path: [index, "scope"],
            });
        }
        scopes.add(mount.scope);
    }
}

export const fileSystemMountsSchema = z
    .array(fileSystemMountSchema)
    .max(16)
    .superRefine((mounts, context) => {
        uniqueNonOverlappingMounts(mounts, context);
        uniqueScopes(mounts, context);
        rejectReservedResultsDiskPaths(mounts, context);
        const ids = new Set<string>();
        for (const [index, mount] of mounts.entries()) {
            if (ids.has(mount.fileSystemId)) {
                context.addIssue({
                    code: "custom",
                    message: "Filesystem IDs must be unique within a sandbox",
                    path: [index, "fileSystemId"],
                });
            }
            ids.add(mount.fileSystemId);
        }
    });

export const fileSystemRequirementsSchema = z
    .record(fileSystemSlotSchema, fileSystemRequirementSchema)
    .superRefine((requirements, context) => {
        rejectReservedResultsSlot(requirements, context);
        const entries = Object.entries(requirements);
        if (entries.length > 16) {
            context.addIssue({
                code: "too_big",
                maximum: 16,
                origin: "object",
                inclusive: true,
                message: "Agents may declare at most 16 filesystems",
            });
        }
        uniqueNonOverlappingMounts(
            entries.map(([, requirement]) => requirement),
            context,
        );
    });

export const fileSystemBindingsSchema = z
    .record(fileSystemSlotSchema, fileSystemRefSchema)
    .refine((bindings) => Object.keys(bindings).length <= 16, {
        message: "A session may bind at most 16 filesystems",
    });

export const resolvedFileSystemMountsSchema = z
    .array(resolvedFileSystemMountSchema)
    .max(17)
    .superRefine((mounts, context) => {
        uniqueNonOverlappingMounts(mounts, context);
        const slots = new Set<string>();
        const ids = new Set<string>();
        for (const [index, mount] of mounts.entries()) {
            if (slots.has(mount.slot)) {
                context.addIssue({
                    code: "custom",
                    message: "Filesystem slots must be unique within a sandbox",
                    path: [index, "slot"],
                });
            }
            slots.add(mount.slot);
            if (ids.has(mount.fileSystem.id)) {
                context.addIssue({
                    code: "custom",
                    message:
                        "Filesystem references must be unique within a sandbox",
                    path: [index, "fileSystem", "id"],
                });
            }
            ids.add(mount.fileSystem.id);
        }
    });

export type FileSystemMount = z.infer<typeof fileSystemMountSchema>;
export type FileSystemRef = z.infer<typeof fileSystemRefSchema>;
export type FileSystemExpectedBinding = z.infer<
    typeof fileSystemExpectedBindingSchema
>;
export type FileSystemRequirement = z.infer<typeof fileSystemRequirementSchema>;
export type FileSystemRequirements = z.infer<
    typeof fileSystemRequirementsSchema
>;
export type AuthoredFileSystemRequirement = z.infer<
    typeof authoredFileSystemRequirementSchema
>;
export type AuthoredFileSystemRequirements = z.infer<
    typeof authoredFileSystemRequirementsSchema
>;
export type FileSystemBindings = z.infer<typeof fileSystemBindingsSchema>;
export type ResolvedFileSystemMount = z.infer<
    typeof resolvedFileSystemMountSchema
>;
